import axios from "../utils/index";
import AxiosBase from "axios";
import type { ApplicantDetailsResponseV2 } from "../interface/applicantDetailsV2";
import type {
  ApplicantDetailsResponse,
  ApplicantDetailsData,
} from "../interface/applicantDetails";
import type {
  HospitalApplicantRequest,
  ApplicantDTO,
  DoctorGroupDTO,
  ShiftGroupDTO,
  PageBean,
  ApplicantListResponse,
  ApplicantGroupBy,
} from "../interface/applicant";

// ============================================================================
// MODERN PROMISE-BASED API FUNCTIONS (Recommended)
// ============================================================================

/**
 * Get a single applicant's detailed information
 * @param shiftsId - The UUID of the Shift Group
 * @param shiftsDaysId - The UUID of the specific shift day
 * @param userId - The UUID of the doctor (applicant)
 * @returns Promise with applicant details or null on error
 */
export const getApplicantDetailsV2 = async (
  shiftsId: string,
  shiftsDaysId: string,
  userId: string,
): Promise<ApplicantDetailsResponseV2["data"] | null> => {
  const fetchApplicant = async (sid: string, sdid: string) => {
    const response = await axios.get<ApplicantDetailsResponseV2>(
      `v2/hospital/shifts/shifts-days-user-get/${sid}/${sdid}/${userId}`,
    );
    if (response.data?.code === 200 && response.data?.data) {
      return response.data.data;
    }
    if (response.data?.message) {
      console.error("API Error:", response.data.message);
    }
    return null;
  };

  try {
    let data = await fetchApplicant(shiftsId, shiftsDaysId);
    if (data) return data;

    const contextResponse = await axios.get(
      `v2/hospital/shifts/shifts-get/${shiftsId}/${shiftsDaysId}`,
    );
    const altShiftsId = contextResponse.data?.data?.shiftsId as string | undefined;
    if (altShiftsId && altShiftsId !== shiftsId) {
      data = await fetchApplicant(altShiftsId, shiftsDaysId);
      if (data) return data;
    }

    return null;
  } catch (error) {
    console.error("Failed to fetch applicant details:", error);
    throw error;
  }
};

/**
 * Decline an applicant for one or more shifts
 * @param shiftsId - The UUID of the Shift Group
 * @param userId - The UUID of the doctor
 * @param keyWord - Comma separated string of shift day IDs to decline
 * @returns Promise with operation result
 */
export const declineApplicationV2 = async (
  shiftsId: string,
  userId: string,
  keyWord: string,
): Promise<{ success: boolean; message?: string }> => {
  try {
    const response = await axios.post(
      `v1/hospital/shifts/shifts-Declined/${shiftsId}/${userId}`,
      { keyWord },
    );

    if (response.data?.code === 200) {
      return { success: true };
    } else {
      return {
        success: false,
        message:
          response.data?.message ||
          response.data?.msg ||
          "Decline operation failed",
      };
    }
  } catch (error) {
    console.error("Failed to decline application:", error);
    return { success: false, message: "Network or server error" };
  }
};

/**
 * Get all applicants for a specific shift day
 * @param shiftsId - The UUID of the Shift Group
 * @param shiftsDaysId - The UUID of the specific shift day
 * @returns Promise with applicants list or null on error
 */
export const getApplicantsListV2 = async (
  shiftsId: string,
  shiftsDaysId: string,
): Promise<ApplicantDetailsData | null> => {
  try {
    const response = await axios.get<ApplicantDetailsResponse>(
      `v1/hospital/shifts/shifts-days-user-get/${shiftsId}/${shiftsDaysId}`,
    );

    if (response.data?.code === 200 && response.data?.data) {
      return response.data.data;
    } else {
      console.error("API Error:", response.data?.message || response.data?.msg);
      return null;
    }
  } catch (error) {
    console.error("Failed to fetch applicants list:", error);
    throw error;
  }
};

/**
 * Add a new shift
 */
export const addShiftV2 = async (params: any): Promise<any> => {
  try {
    const response = await axios.post(`v1/hospital/shifts/shifts-add`, params);
    if (response.data?.code === 200) {
      return response.data.data;
    } else {
      throw new Error(response.data?.message || "Failed to add shift");
    }
  } catch (error) {
    console.error("Failed to add shift:", error);
    throw error;
  }
};

/**
 * Edit an existing shift
 */
export const editShiftV2 = async (
  shiftsId: string,
  data: any,
): Promise<any> => {
  try {
    const response = await axios.put(
      `v1/hospital/shifts/shifts-edit/${shiftsId}`,
      data,
    );
    if (response.data?.code === 200) {
      return response.data.data;
    } else {
      throw new Error(response.data?.message || "Failed to edit shift");
    }
  } catch (error) {
    console.error("Failed to edit shift:", error);
    throw error;
  }
};

/**
 * List shifts with filtering and pagination
 */
export const listShiftsV2 = async (params: {
  flag: string;
  start: number;
  end: number;
  page: number;
  pageSize: number;
  keyWord?: string;
  filterType?: string;
  cancelToken?: any;
}): Promise<any> => {
  try {
    const payload: any = { keyWord: params.keyWord || "" };
    if (params.filterType) {
      payload.filterType = params.filterType;
    }

    const response = await axios.post(
      `v1/hospital/shifts/shifts-list/${params.flag}/${params.start}/${params.end}/${params.page}/${params.pageSize}`,
      payload,
      { cancelToken: params.cancelToken },
    );
    if (response.data?.code === 200) {
      return response.data.data;
    } else {
      throw new Error(response.data?.message || "Failed to list shifts");
    }
  } catch (error) {
    console.error("Failed to list shifts:", error);
    throw error;
  }
};

/**
 * Get detailed information for a shift and its applicants (V2 API)
 * New simplified endpoint for the Right Panel details
 */
export const getShiftApplicantsV2 = async (
  shiftsDaysId: string,
): Promise<any> => {
  try {
    const response = await axios.get(
      `v2/hospital/shifts/shift-applicants/${shiftsDaysId}`,
    );
    if (response.data?.code === 200) {
      return response.data.data;
    } else {
      throw new Error(
        response.data?.message || "Failed to get shift applicants",
      );
    }
  } catch (error) {
    console.error("Failed to get shift applicants:", error);
    throw error;
  }
};

/**
 * Get detailed split view of a shift context (Current Day vs All Days in Group)
 * GET /web/v2/hospital/shifts/shifts-get/{shiftsId}/{shiftsDaysId}
 */
export const getShiftDetails = async (
  shiftsId: string,
  shiftsDaysId: string,
): Promise<import("../interface/shift").DetailedShiftContext | null> => {
  try {
    const response = await axios.get(
      `v2/hospital/shifts/shifts-get/${shiftsId}/${shiftsDaysId}`,
    );
    if (response.data?.code === 200) {
      return response.data.data;
    } else {
      console.error("API Error:", response.data?.message);
      return null;
    }
  } catch (error) {
    console.error("Failed to fetch detailed shift context:", error);
    throw error;
  }
};

/**
 * Get detailed information for a specific applicant on a shift day (V2)
 * Used as the primary endpoint for the new unified popup widget
 */
export const getShiftDayUserV2 = async (
  shiftsId: string,
  shiftsDaysId: string,
  userId: string,
): Promise<any> => {
  try {
    const response = await axios.get(
      `v2/hospital/shifts/shifts-days-user-get/${shiftsId}/${shiftsDaysId}/${userId}`,
    );
    if (response.data?.code === 200) {
      return response.data.data;
    } else {
      throw new Error(
        response.data?.message || "Failed to get shift user details",
      );
    }
  } catch (error) {
    console.error("Failed to get shift user details:", error);
    throw error;
  }
};

/**
 * Get detailed information for a shift
 */
export const getShiftDetailsV2 = async (
  shiftsId: string,
  shiftsDaysId?: string,
): Promise<any> => {
  try {
    const endpoint = shiftsDaysId
      ? `v1/hospital/shifts/shifts-get/${shiftsId}/${shiftsDaysId}`
      : `v1/hospital/shifts/shifts-get/${shiftsId}`;
    const response = await axios.get(endpoint);
    if (response.data?.code === 200) {
      return response.data.data;
    } else {
      console.error("getShiftDetailsV2 API error:", response.data);
      throw new Error(response.data?.message || "Failed to get shift details");
    }
  } catch (error) {
    console.error("getShiftDetailsV2 exception:", error);
    throw error;
  }
};

/**
 * Delete a single date from a shift
 */
export const deleteSingleShiftDateV2 = async (
  shiftsId: string,
  shiftsDaysId: string,
): Promise<any> => {
  try {
    const response = await axios.delete(
      `v1/hospital/shifts/shifts-remove/${shiftsId}/${shiftsDaysId}`,
    );
    if (response.data?.code === 200) {
      return response.data.data;
    } else {
      throw new Error(response.data?.message || "Failed to delete shift date");
    }
  } catch (error) {
    console.error("Failed to delete shift date:", error);
    throw error;
  }
};

/**
 * Delete all dates from a shift
 */
export const deleteAllShiftDatesV2 = async (shiftsId: string): Promise<any> => {
  try {
    const response = await axios.delete(
      `v1/hospital/shifts/shifts-remove/${shiftsId}`,
    );
    if (response.data?.code === 200) {
      return response.data.data;
    } else {
      throw new Error(response.data?.message || "Failed to delete shift");
    }
  } catch (error) {
    console.error("Failed to delete shift:", error);
    throw error;
  }
};

/**
 * Remove a doctor from a shift
 */
export const removeDoctorFromShiftV2 = async (
  ordersId: string,
  shiftsId: string,
  shiftsDaysId: string,
): Promise<any> => {
  try {
    const response = await axios.put(
      `v1/hospital/shifts/shifts-cancel/${ordersId}/${shiftsId}/${shiftsDaysId}`,
    );
    if (response.data?.code === 200) {
      return response.data.data;
    } else {
      throw new Error(response.data?.message || "Failed to remove doctor");
    }
  } catch (error) {
    console.error("Failed to remove doctor:", error);
    throw error;
  }
};

/**
 * Get doctor information
 */
export const getDoctorV2 = async (userId: string): Promise<any> => {
  try {
    const response = await axios.get(
      `v1/hospital/user/users-info-get/${userId}`,
    );
    if (response.data?.code === 200) {
      return response.data.data;
    } else {
      throw new Error(response.data?.message || "Failed to get doctor info");
    }
  } catch (error) {
    console.error("Failed to get doctor info:", error);
    throw error;
  }
};

/**
 * Set a doctor as favorite
 */
export const setFavoriteDoctorV2 = async (userId: string): Promise<any> => {
  try {
    const response = await axios.post(
      `v1/hospital/favourites/favourites-add/${userId}`,
    );
    if (response.data?.code === 200) {
      return response.data.data;
    } else {
      throw new Error(response.data?.message || "Failed to set favorite");
    }
  } catch (error) {
    console.error("Failed to set favorite:", error);
    throw error;
  }
};

/**
 * Remove a doctor from favorites
 */
export const removeFavoriteDoctorV2 = async (userId: string): Promise<any> => {
  try {
    const response = await axios.delete(
      `v1/hospital/favourites/favourites-removed/${userId}`,
    );
    if (response.data?.code === 200) {
      return response.data.data;
    } else {
      throw new Error(response.data?.message || "Failed to remove favorite");
    }
  } catch (error) {
    console.error("Failed to remove favorite:", error);
    throw error;
  }
};

/**
 * Get all dates for a shift
 */
export const getShiftAllDatesV2 = async (shiftsId: string): Promise<any> => {
  try {
    const response = await axios.get(
      `v1/hospital/shifts/shifts-get/${shiftsId}`,
    );
    if (response.data?.code === 200) {
      return response.data.data;
    } else {
      throw new Error(response.data?.message || "Failed to get shift dates");
    }
  } catch (error) {
    console.error("Failed to get shift dates:", error);
    throw error;
  }
};

/**
 * Re-advertise a shift
 */
export const reAdvertiseShiftV2 = async (
  shiftsId: string,
  shiftsDaysId: string,
): Promise<any> => {
  try {
    const response = await axios.post(
      `v1/hospital/shifts/shifts-Re-Advertise/${shiftsId}/${shiftsDaysId}`,
    );
    if (response.data?.code === 200) {
      return response.data.data;
    } else {
      throw new Error(response.data?.message || "Failed to re-advertise shift");
    }
  } catch (error) {
    console.error("Failed to re-advertise shift:", error);
    throw error;
  }
};

/**
 * Check unconfirmed applicants
 */
export const checkUnconfirmedApplicantsV2 = async (): Promise<any> => {
  try {
    const response = await axios.get(
      `v1/hospital/shifts/shifts-days-user-cancel-unCheck`,
    );
    if (response.data?.code === 200) {
      return response.data.data;
    } else {
      throw new Error(response.data?.message || "Failed to check applicants");
    }
  } catch (error) {
    console.error("Failed to check applicants:", error);
    throw error;
  }
};

/**
 * Download a file
 */
export const downloadFileV2 = async (params: any): Promise<any> => {
  try {
    const response = await axios.post(`v1/public/upload/download`, params);
    if (response.data?.code === 200) {
      return response.data.data;
    } else {
      throw new Error(response.data?.message || "Failed to download file");
    }
  } catch (error) {
    console.error("Failed to download file:", error);
    throw error;
  }
};

/**
 * Bulk add shifts
 */
export const bulkAddShiftsV2 = async (
  params: any,
  options?: { hospitalId?: string },
): Promise<any> => {
  try {
    const headers: Record<string, string> = {};
    if (options?.hospitalId) {
      headers["X-Hospital-Id"] = options.hospitalId;
    }
    const response = await axios.post(
      `v1/hospital/shifts/bulk/shifts-add`,
      params,
      Object.keys(headers).length ? { headers } : undefined,
    );
    if (response.data?.code === 200) {
      return response.data.data;
    } else {
      throw new Error(response.data?.message || "Failed to bulk add shifts");
    }
  } catch (error) {
    console.error("Failed to bulk add shifts:", error);
    throw error;
  }
};

/**
 * Transform raw applicant data from API to ActiveApplicant interface
 * Handles both old V1 field names and new unified API field names
 */
const transformApplicantData = (data: any[]): any[] => {
  if (!Array.isArray(data)) {
    return [];
  }

  return data.map((item) => ({
    // Application Info
    orderId: item.orderId || item.ordersId || "",
    applyTime: item.applyTime || 0,
    applyDateString: item.applyDateString || "",
    isNew: item.isNew ?? item.newApplicant ?? false,
    isInvited: item.isInvited ?? false,
    status: item.status || "PENDING",

    // Doctor Info
    doctorId: item.doctorId || item.userId || "",
    doctorName:
      item.doctorName ||
      (item.firstName && item.surname
        ? `${item.firstName} ${item.surname}`
        : ""),
    doctorImageUrl: item.doctorImageUrl || item.profilePic || "",
    doctorSpecialty: item.doctorSpecialty || item.specialty || "",
    doctorSpecialtyName: item.doctorSpecialtyName || item.specialtyName || "",
    doctorSkillLevel: item.doctorSkillLevel || item.skillLevel || "",
    doctorSkillLevelName:
      item.doctorSkillLevelName || item.skillLevelName || "",
    doctorRating: item.doctorRating ?? item.rating ?? null,
    isFavourite: item.isFavourite ?? item.isFavorite ?? false,
    hasWorkedAtHospital: item.hasWorkedAtHospital ?? item.workedBefore ?? false,

    // Match Score
    matchScore: item.matchScore ?? 0,
    matchScoreReason: item.matchScoreReason || "",

    // History at this hospital
    history: item.history || {
      completedShifts: 0,
      cancelledShifts: 0,
      lastShiftDate: "",
    },

    // Doctor Statistics (Global)
    statistics: item.statistics || null,

    // Shift History
    shiftHistory: item.shiftHistory || [],

    // Ratings Summary
    ratingSummary: item.ratingSummary || null,

    // Shift Info
    shiftId: item.shiftId || item.shiftsId || "",
    shiftDayId: item.shiftDayId || item.shiftsDaysId || "",
    shiftNo: item.shiftNo || item.shiftsNo || "",
    shiftStartTime: item.shiftStartTime || item.startTime || 0,
    shiftDate: item.shiftDate || item.shiftsDay || 0,
    shiftDateString: item.shiftDateString || "",
    shiftRole: item.shiftRole || item.roleId || "",
    shiftRoleName: item.shiftRoleName || item.roleName || "",
    shiftSpecialty: item.shiftSpecialty || "",
    shiftSpecialtyName: item.shiftSpecialtyName || "",
    hospitalName: item.hospitalName || "",
    isGroupShift:
      item.isGroupShift ??
      (item.shiftsOnGroup && item.shiftsOnGroup > 0) ??
      false,
    appliedDaysCount: item.appliedDaysCount ?? 0,

    // Pre-credential enrichment fields
    preCredentialType: item.preCredentialType,
    preCredentialed: item.preCredentialed ?? false,
    preCredentialStatus: item.preCredentialStatus,
    preCredentialMatch: item.preCredentialMatch,
    preCredentialMatchedSpecialties: Array.isArray(
      item.preCredentialMatchedSpecialties,
    )
      ? item.preCredentialMatchedSpecialties
      : [],
    preCredentialMatchedSkillLevel: item.preCredentialMatchedSkillLevel,
  }));
};

/**
 * Get active applicants (V2 API) - Now uses unified endpoint
 */
export const getActiveApplicantsV2 = async (params: any = {}): Promise<any> => {
  try {
    const {
      page = 1,
      pageSize = 10,
      keyWord = "",
      groupBy = null,
      sortBy = "Date",
      hospitalIds,
    } = params;

    // Normalize groupBy to lowercase for consistent checking
    const normalizedGroupBy = groupBy ? groupBy.toLowerCase() : null;

    // Use unified endpoint with tab=ACTIVE
    const body: any = {
      tab: "ACTIVE",
      pageNo: page,
      pageSize,
      keyWord,
      groupBy: normalizedGroupBy === "none" ? null : normalizedGroupBy, // Convert "none" to null for flat list
      sortBy,
    };
    if (hospitalIds) body.hospitalIds = hospitalIds;

    const response = await axios.post(`v2/hospital/applicants/list`, body);

    if (response.data?.code === 200 || response.data?.code === 0) {
      const data = response.data.data;

      // Handle PageBean structure with rows OR list (API uses "list" not "rows")
      const rows = data?.rows || data?.list;
      if (data && rows && Array.isArray(rows)) {
        // Check if it's grouped by doctor or shift (use normalized for case-insensitive comparison)
        if (normalizedGroupBy === "doctor") {
          return {
            list: rows, // DoctorGroupDTO[]
            total: data.total ?? data.totalRecord ?? rows.length,
            grouped: true,
            groupType: "doctor",
          };
        } else if (normalizedGroupBy === "shift") {
          return {
            list: rows, // ShiftGroupDTO[]
            total: data.total ?? data.totalRecord ?? rows.length,
            grouped: true,
            groupType: "shift",
          };
        } else {
          // Flat list - transform to ActiveApplicant format
          return {
            list: transformApplicantData(rows),
            total: data.total ?? data.totalRecord ?? rows.length,
          };
        }
      }

      // Fallback: Handle old response formats
      if (Array.isArray(data)) {
        return { list: transformApplicantData(data), total: data.length };
      } else if (data && typeof data === "object") {
        const list = data.records || data.items || data.content || [];
        return {
          list: transformApplicantData(list),
          total: data.total || data.totalElements || list.length,
        };
      }
      return { list: [], total: 0 };
    } else {
      throw new Error(
        response.data?.message ||
          response.data?.msg ||
          "Failed to get active applicants",
      );
    }
  } catch (error: any) {
    console.error(
      "Failed to get active applicants:",
      error?.response?.data || error,
    );
    throw error;
  }
};

/**
 * Get past applicants (V2 API) - Now uses unified endpoint
 */
export const getPastApplicantsV2 = async (params: any = {}): Promise<any> => {
  try {
    const {
      page = 1,
      pageSize = 10,
      keyWord = "",
      groupBy = null,
      sortBy = "Date",
      hospitalIds,
    } = params;

    // Normalize groupBy to lowercase for consistent checking
    const normalizedGroupBy = groupBy ? groupBy.toLowerCase() : null;

    // Use unified endpoint with tab=PAST
    const body: any = {
      tab: "PAST",
      pageNo: page,
      pageSize,
      keyWord,
      groupBy: normalizedGroupBy === "none" ? null : normalizedGroupBy, // Convert "none" to null for flat list
      sortBy,
    };
    if (hospitalIds) body.hospitalIds = hospitalIds;

    const response = await axios.post(`v2/hospital/applicants/list`, body);

    if (response.data?.code === 200 || response.data?.code === 0) {
      const data = response.data.data;

      // Handle PageBean structure with rows OR list (API uses "list" not "rows")
      const rows = data?.rows || data?.list;
      if (data && rows && Array.isArray(rows)) {
        // Check if it's grouped by doctor or shift
        if (normalizedGroupBy === "doctor") {
          return {
            list: rows, // DoctorGroupDTO[]
            total: data.total ?? data.totalRecord ?? rows.length,
            grouped: true,
            groupType: "doctor",
          };
        } else if (normalizedGroupBy === "shift") {
          return {
            list: rows, // ShiftGroupDTO[]
            total: data.total ?? data.totalRecord ?? rows.length,
            grouped: true,
            groupType: "shift",
          };
        } else {
          // Flat list - transform to ActiveApplicant format
          return {
            list: transformApplicantData(rows),
            total: data.total ?? data.totalRecord ?? rows.length,
          };
        }
      }

      // Fallback: Handle old response formats
      if (Array.isArray(data)) {
        return { list: transformApplicantData(data), total: data.length };
      } else if (data && typeof data === "object") {
        const list = data.records || data.items || data.content || [];
        return {
          list: transformApplicantData(list),
          total: data.total || data.totalElements || list.length,
        };
      }
      return { list: [], total: 0 };
    } else {
      throw new Error(
        response.data?.message ||
          response.data?.msg ||
          "Failed to get past applicants",
      );
    }
  } catch (error: any) {
    console.error(
      "Failed to get past applicants:",
      error?.response?.data || error,
    );
    throw error;
  }
};

// ============================================================================
// UNIFIED APPLICANTS API (v2/hospital/applicants/list)
// ============================================================================

/** Result type based on groupBy parameter */
export type UnifiedApplicantsResult<T extends ApplicantGroupBy> =
  T extends "doctor"
    ? {
        rows: DoctorGroupDTO[];
        total: number;
        pageNo: number;
        pageSize: number;
      }
    : T extends "shift"
      ? {
          rows: ShiftGroupDTO[];
          total: number;
          pageNo: number;
          pageSize: number;
        }
      : {
          rows: ApplicantDTO[];
          total: number;
          pageNo: number;
          pageSize: number;
        };

/**
 * Get applicants list (Unified V2 API)
 *
 * The primary endpoint for retrieving applicants. Controls Active vs Past views
 * via the `tab` parameter and supports hierarchical grouping.
 *
 * @param request - The request parameters
 * @returns Promise with paginated applicant data based on groupBy parameter
 *
 * @example
 * // Get active applicants grouped by doctor
 * const result = await getApplicantsUnified({
 *   tab: "ACTIVE",
 *   groupBy: "doctor",
 *   pageNo: 1,
 *   pageSize: 20
 * });
 *
 * @example
 * // Get past applicants as flat list
 * const result = await getApplicantsUnified({
 *   tab: "PAST",
 *   groupBy: null,
 *   keyWord: "John",
 *   sortBy: "Date"
 * });
 */
export const getApplicantsUnified = async <T extends ApplicantGroupBy = null>(
  request: HospitalApplicantRequest,
): Promise<UnifiedApplicantsResult<T>> => {
  try {
    const {
      tab,
      groupBy = null,
      hospitalIds,
      pageNo = 1,
      pageSize = 20,
      keyWord = "",
      sortBy = "Date",
    } = request;

    const body: Record<string, any> = {
      tab,
      groupBy,
      pageNo,
      pageSize,
      keyWord,
      sortBy,
    };

    if (hospitalIds && hospitalIds.length > 0) {
      body.hospitalIds = hospitalIds;
    }

    const response = await axios.post<ApplicantListResponse<any>>(
      `/v2/hospital/applicants/list`,
      body,
    );

    if (response.data?.code === 200 || response.data?.code === 0) {
      const data = response.data.data as any;

      // Handle PageBean structure
      if (data && typeof data === "object") {
        const rows =
          data.rows ||
          data.list ||
          data.records ||
          data.items ||
          data.content ||
          [];
        const total =
          data.total ?? data.totalRecord ?? data.totalElements ?? rows.length;
        const respPageNo = data.pageNo ?? data.page ?? pageNo;
        const respPageSize = data.pageSize ?? data.size ?? pageSize;

        return {
          rows,
          total,
          pageNo: respPageNo,
          pageSize: respPageSize,
        } as UnifiedApplicantsResult<T>;
      }

      // Fallback for array response
      if (Array.isArray(data)) {
        return {
          rows: data,
          total: data.length,
          pageNo,
          pageSize,
        } as UnifiedApplicantsResult<T>;
      }

      return {
        rows: [],
        total: 0,
        pageNo,
        pageSize,
      } as unknown as UnifiedApplicantsResult<T>;
    } else {
      throw new Error(
        response.data?.message ||
          response.data?.msg ||
          "Failed to get applicants",
      );
    }
  } catch (error: any) {
    console.error(
      "Failed to get applicants (unified):",
      error?.response?.data || error,
    );
    throw error;
  }
};

/**
 * Get active applicants using the unified API
 * Convenience wrapper for getApplicantsUnified with tab="ACTIVE"
 */
export const getActiveApplicantsUnified = async <
  T extends ApplicantGroupBy = null,
>(
  params: Omit<HospitalApplicantRequest, "tab"> = {},
): Promise<UnifiedApplicantsResult<T>> => {
  return getApplicantsUnified<T>({ ...params, tab: "ACTIVE" });
};

/**
 * Get past applicants using the unified API
 * Convenience wrapper for getApplicantsUnified with tab="PAST"
 */
export const getPastApplicantsUnified = async <
  T extends ApplicantGroupBy = null,
>(
  params: Omit<HospitalApplicantRequest, "tab"> = {},
): Promise<UnifiedApplicantsResult<T>> => {
  return getApplicantsUnified<T>({ ...params, tab: "PAST" });
};

/**
 * Get applicants grouped by doctor
 * Convenience wrapper for getApplicantsUnified with groupBy="doctor"
 */
export const getApplicantsGroupedByDoctor = async (
  params: Omit<HospitalApplicantRequest, "groupBy">,
): Promise<UnifiedApplicantsResult<"doctor">> => {
  return getApplicantsUnified<"doctor">({ ...params, groupBy: "doctor" });
};

/**
 * Get applicants grouped by shift
 * Convenience wrapper for getApplicantsUnified with groupBy="shift"
 */
export const getApplicantsGroupedByShift = async (
  params: Omit<HospitalApplicantRequest, "groupBy">,
): Promise<UnifiedApplicantsResult<"shift">> => {
  return getApplicantsUnified<"shift">({ ...params, groupBy: "shift" });
};

/**
 * List shifts (V2 API) with advanced options
 */
export const listShiftsV2Advanced = async (params: {
  tab?: string;
  grouped?: boolean;
  sortBy?: string;
  pageNo?: number;
  pageSize?: number;
  keyWord?: string;
  startTime?: number;
  endTime?: number;
  filterType?: string;
  specialty?: string;
  skillLevel?: string;
  onCall?: boolean | null;
  rateType?: string;
  cancelToken?: any;
}): Promise<any> => {
  try {
    const {
      tab = "Active",
      grouped = false,
      sortBy = "Recommended",
      pageNo = 1,
      pageSize = 10,
      keyWord = "",
      startTime = 0,
      endTime = 9999999999,
      filterType,
      specialty,
      skillLevel,
      onCall,
      rateType,
      cancelToken = null,
    } = params;

    // Fix: Ensure we don't end up with double slashes if baseURL ends with /
    let url = `v2/hospital/shifts/shifts-list/${tab}?grouped=${grouped}&sortBy=${sortBy}&pageNo=${pageNo}&pageSize=${pageSize}&startTime=${startTime}&endTime=${endTime}`;
    if (filterType) {
      url += `&filterType=${filterType}`;
    }

    const body: Record<string, unknown> = { keyWord };
    if (specialty) body.specialty = specialty;
    if (skillLevel) body.skillLevel = skillLevel;
    if (onCall === true || onCall === false) body.onCall = onCall;
    if (rateType) body.rateType = rateType;

    const response = await axios.post(url, body, { cancelToken });
    if (response.data?.code === 200) {
      return response.data.data;
    } else {
      throw new Error(response.data?.message || "Failed to list shifts");
    }
  } catch (error) {
    console.error("Failed to list shifts:", error);
    throw error;
  }
};

export interface HospitalAiQueueItem {
  id: string;
  hospitalId?: string;
  hospitalName?: string;
  shiftDate?: number;
  confidencePercentage?: number;
  shiftData?: string;
  source?: string;
  documentUrl?: string;
  createTime?: number;
  updateTime?: number;
}

export interface HospitalAiQueueListResponse {
  list: HospitalAiQueueItem[];
  total: number;
  pageNum: number;
  pageSize: number;
  pages: number;
}

export interface HospitalAiQueueGroupedItem {
  batchGroupId: string;
  hospitalId?: string;
  hospitalName?: string;
  documentUrl?: string;
  source?: string;
  shiftCount?: number;
  minShiftDate?: number;
  maxShiftDate?: number;
  firstCreateTime?: number;
  lastCreateTime?: number;
  items?: HospitalAiQueueItem[];
}

export interface HospitalAiQueueGroupedListResponse {
  list: HospitalAiQueueGroupedItem[];
  total: number;
  pageNum: number;
  pageSize: number;
  pages: number;
}

type AiQueueRequestOptions = {
  pageNum?: number;
  pageSize?: number;
  includeItems?: boolean;
  debug?: boolean;
  cancelToken?: any;
  /** Use master login token so queue spans the whole hospital group (Global Active). */
  masterScope?: boolean;
  inboxChannel?: "genuine" | "spam" | "all";
};

function getAiQueueApiBaseUrl(): string {
  return axios.defaults.baseURL || process.env.REACT_APP_URL || "";
}

function buildMasterScopeAiQueueHeaders(): Record<string, string> | null {
  const token =
    localStorage.getItem("master-token") || localStorage.getItem("token-name");
  if (!token) return null;
  return {
    "doctor-admin-token": token,
    "Accept-Language": "en-US",
  };
}

const unwrapAiQueuePayload = <T,>(
  response: { data?: { code?: number; data?: T; message?: string; list?: unknown } },
): T | null => {
  const body = response?.data;
  if (!body) return null;
  const code = Number(body.code);
  const payload = (body.data ?? (Array.isArray(body.list) ? body : null)) as T | null;
  if (!payload) return null;
  if (code === 200 || code === 0 || Number.isNaN(code)) return payload;
  return null;
};

async function fetchHospitalAiQueueEndpoint<T>(
  path: string,
  queryParams: Record<string, unknown>,
  options: Pick<AiQueueRequestOptions, "cancelToken" | "masterScope"> = {},
): Promise<{ data: { code?: number; data?: T; message?: string } }> {
  const { cancelToken = null, masterScope = false } = options;
  const masterHeaders = masterScope ? buildMasterScopeAiQueueHeaders() : null;

  if (masterScope && masterHeaders) {
    const baseURL = getAiQueueApiBaseUrl();
    const url = baseURL ? `${baseURL}/${path.replace(/^\//, "")}` : path;
    return AxiosBase.get(url, {
      params: queryParams,
      cancelToken,
      headers: masterHeaders,
      timeout: 45000,
    });
  }

  return axios.get(path, {
    params: queryParams,
    cancelToken,
    timeout: 45000,
  });
}

async function postHospitalAiQueueEndpoint<T>(
  path: string,
  body: unknown,
  options: Pick<AiQueueRequestOptions, "masterScope"> = {},
): Promise<{ data: { code?: number; data?: T; message?: string } }> {
  const { masterScope = false } = options;
  const masterHeaders = masterScope ? buildMasterScopeAiQueueHeaders() : null;

  if (masterScope && masterHeaders) {
    const baseURL = getAiQueueApiBaseUrl();
    const url = baseURL ? `${baseURL}/${path.replace(/^\//, "")}` : path;
    return AxiosBase.post(url, body, { headers: masterHeaders });
  }

  return axios.post(path, body);
}

export const listHospitalAiQueue = async (
  params: AiQueueRequestOptions = {},
): Promise<HospitalAiQueueListResponse> => {
  try {
    const {
      pageNum = 1,
      pageSize = 10,
      cancelToken = null,
      masterScope = false,
    } = params;

    const response = await fetchHospitalAiQueueEndpoint<HospitalAiQueueListResponse>(
      "v2/hospital/ai/queue/list",
      { pageNum, pageSize },
      { cancelToken, masterScope },
    );

    if (response.data?.code === 200 && response.data?.data) {
      return response.data.data;
    }

    throw new Error(response.data?.message || "Failed to fetch AI shift queue");
  } catch (error) {
    console.error("Failed to fetch AI shift queue:", error);
    throw error;
  }
};

/** Lightweight pending-row count for Hire nav badge (skips grouped enrichment). */
export const getHospitalAiQueueStats = async (
  params: Pick<AiQueueRequestOptions, "cancelToken" | "masterScope"> = {},
): Promise<{ totalQueueItems: number }> => {
  const { cancelToken = null, masterScope = false } = params;
  const response = await fetchHospitalAiQueueEndpoint<{
    totalQueueItems?: number;
  }>("v2/hospital/ai/queue/stats", {}, { cancelToken, masterScope });

  if (response.data?.code === 200 && response.data?.data) {
    return {
      totalQueueItems: Number(response.data.data.totalQueueItems) || 0,
    };
  }

  throw new Error(response.data?.message || "Failed to fetch AI queue stats");
};

export const listHospitalAiQueueGrouped = async (
  params: AiQueueRequestOptions = {},
): Promise<HospitalAiQueueGroupedListResponse> => {
  try {
    const {
      pageNum = 1,
      pageSize = 10,
      includeItems = true,
      debug = false,
      cancelToken = null,
      masterScope = false,
      inboxChannel,
    } = params;

    const response =
      await fetchHospitalAiQueueEndpoint<HospitalAiQueueGroupedListResponse>(
        "v2/hospital/ai/queue/grouped-list",
        {
          pageNum,
          pageSize,
          includeItems,
          debug,
          ...(inboxChannel ? { inboxChannel } : {}),
        },
        { cancelToken, masterScope },
      );

    const data = unwrapAiQueuePayload<HospitalAiQueueGroupedListResponse>(response);
    if (data) return data;

    throw new Error(
      response.data?.message || "Failed to fetch grouped AI shift queue",
    );
  } catch (error) {
    if (AxiosBase.isCancel(error)) throw error;
    if (params.inboxChannel) {
      try {
        const retry = await fetchHospitalAiQueueEndpoint<HospitalAiQueueGroupedListResponse>(
          "v2/hospital/ai/queue/grouped-list",
          {
            pageNum: params.pageNum ?? 1,
            pageSize: params.pageSize ?? 10,
            includeItems: params.includeItems ?? true,
            debug: params.debug ?? false,
          },
          { cancelToken: params.cancelToken, masterScope: params.masterScope },
        );
        const data = unwrapAiQueuePayload<HospitalAiQueueGroupedListResponse>(retry);
        if (data) return data;
      } catch (retryError) {
        if (AxiosBase.isCancel(retryError)) throw retryError;
      }
    }
    console.error("Failed to fetch grouped AI shift queue:", error);
    throw error;
  }
};

export const listHospitalAiHistoryGrouped = async (
  params: AiQueueRequestOptions = {},
): Promise<HospitalAiQueueGroupedListResponse> => {
  try {
    const {
      pageNum = 1,
      pageSize = 10,
      includeItems = true,
      cancelToken = null,
      masterScope = false,
    } = params;

    const response =
      await fetchHospitalAiQueueEndpoint<HospitalAiQueueGroupedListResponse>(
        "v2/hospital/ai/history/grouped-list",
        { pageNum, pageSize, includeItems },
        { cancelToken, masterScope },
      );

    if (response.data?.code === 200 && response.data?.data) {
      return response.data.data;
    }

    throw new Error(
      response.data?.message || "Failed to fetch grouped AI shift history",
    );
  } catch (error) {
    console.error("Failed to fetch grouped AI shift history:", error);
    throw error;
  }
};

export interface HospitalAiQueueActionResponse {
  approvedCount?: number;
  approvedIds?: string[];
  rejectedCount?: number;
  rejectedIds?: string[];
  batchGroupId?: string;
  movedToHistory?: boolean;
  deletedFromQueue?: boolean;
}

export interface ApproveHospitalAiQueueItemPayload {
  approvedDates?: string[];
  /** Use master login token (same as Global Active list). */
  masterScope?: boolean;
}

export const approveHospitalAiQueueItem = async (
  queueId: string,
  payload?: ApproveHospitalAiQueueItemPayload,
): Promise<HospitalAiQueueActionResponse> => {
  try {
    const hasApprovedDates =
      Array.isArray(payload?.approvedDates) &&
      payload!.approvedDates.length > 0;
    const masterScope = Boolean(payload?.masterScope);

    const response = await postHospitalAiQueueEndpoint<HospitalAiQueueActionResponse>(
      `v2/hospital/ai/queue/${queueId}/approve`,
      hasApprovedDates ? { approvedDates: payload!.approvedDates } : undefined,
      { masterScope },
    );

    if (response.data?.code === 200 && response.data?.data) {
      return response.data.data;
    }

    throw new Error(
      response.data?.message || "Failed to approve AI queue item",
    );
  } catch (error) {
    console.error("Failed to approve AI queue item:", error);
    throw error;
  }
};

export interface RouteHospitalAiQueueItemPayload {
  targetHospitalId: string;
  targetHospitalName?: string;
  masterScope?: boolean;
}

export const routeHospitalAiQueueItem = async (
  queueId: string,
  payload: RouteHospitalAiQueueItemPayload,
): Promise<HospitalAiQueueActionResponse> => {
  try {
    const response = await postHospitalAiQueueEndpoint<HospitalAiQueueActionResponse>(
      `v2/hospital/ai/queue/${queueId}/route`,
      {
        targetHospitalId: payload.targetHospitalId,
        targetHospitalName: payload.targetHospitalName,
      },
      { masterScope: Boolean(payload.masterScope) },
    );

    if (response.data?.code === 200 && response.data?.data) {
      return response.data.data;
    }

    throw new Error(response.data?.message || "Failed to route AI queue item");
  } catch (error) {
    console.error("Failed to route AI queue item:", error);
    throw error;
  }
};

export const rejectHospitalAiQueueItem = async (
  queueId: string,
  options: Pick<AiQueueRequestOptions, "masterScope"> = {},
): Promise<HospitalAiQueueActionResponse> => {
  try {
    const response = await postHospitalAiQueueEndpoint<HospitalAiQueueActionResponse>(
      `v2/hospital/ai/queue/${queueId}/reject`,
      undefined,
      { masterScope: Boolean(options.masterScope) },
    );

    if (response.data?.code === 200 && response.data?.data) {
      return response.data.data;
    }

    throw new Error(response.data?.message || "Failed to reject AI queue item");
  } catch (error) {
    console.error("Failed to reject AI queue item:", error);
    throw error;
  }
};

/**
 * Upload a shift document (PDF/Excel/CSV/Word) for AI parsing and queueing.
 * POST /v2/hospital/ai/document/upload
 */
export const uploadHospitalAiShiftDocument = async (
  file: File,
  options?: { queue?: boolean },
): Promise<any> => {
  const formData = new FormData();
  formData.append("file", file);
  const queue = options?.queue ?? false;

  try {
    const response = await axios.post(
      `v2/hospital/ai/document/upload?queue=${queue ? "true" : "false"}`,
      formData,
      {
        headers: { "Content-Type": "multipart/form-data" },
      },
    );

    if (response.data?.code === 200) {
      return response.data.data;
    }

    throw new Error(
      response.data?.message || "Failed to upload shift document",
    );
  } catch (error) {
    console.error("Failed to upload shift document:", error);
    throw error;
  }
};

export const getAiEmailAgent = async (options?: {
  hospitalId?: string;
}): Promise<any> => {
  try {
    const headers: Record<string, string> = {};
    if (options?.hospitalId) {
      headers["X-Hospital-Id"] = options.hospitalId;
    }
    const response = await axios.get(
      `v2/hospital/ai/email-agent`,
      Object.keys(headers).length ? { headers } : undefined,
    );
    if (response.data?.code === 200) {
      return response.data.data ?? null;
    }
    throw new Error(
      response.data?.message || "Failed to fetch AI email agent config",
    );
  } catch (error) {
    console.error("Failed to fetch AI email agent config:", error);
    throw error;
  }
};

export interface AiEmailAgentPayload {
  emailList?: string[];
  emailAddresses?: string;
  trustedSenderDomains?: string;
  trustedDomainList?: string[];
  domainList?: string[];
  acceptUnregisteredSenders?: boolean;
  fallbackSpecialty?: string | null;
  fallbackSkillLevel?: string | null;
  fallbackSupportLevel?: string | null;
  fallbackAccommodation?: string | null;
  fallbackTravel?: string | null;
  fallbackShiftTime?: string | null;
  fallbackDescription?: string | null;
  fallbackRateType?: string | null;
  fallbackRate?: number | string | null;
  fallbackAdditionalFilesRequired?: string[];
}

export interface AiEmailAgentCheckPayload {
  emailAddresses?: string;
  emailList?: string[];
}

export interface AiEmailAgentDomainCheckPayload {
  trustedSenderDomains?: string;
  trustedDomainList?: string[];
  domainList?: string[];
}

export interface AiEmailAgentDomainCheckResponse {
  valid?: boolean;
  hasConflict?: boolean;
  checkedDomains?: string[];
  conflictDomains?: string[];
  conflicts?: Record<string, string>;
  details?: Array<{
    domain?: string;
    exists?: boolean;
    usedByHospital?: string;
  }>;
  message?: string;
}

export interface AiEmailAgentCheckResponse {
  valid?: boolean;
  hasConflict?: boolean;
  checkedEmails?: string[];
  conflictEmails?: string[];
  conflicts?: Record<
    string,
    { hospitalName?: string; hospitalId?: string } | string
  >;
  details?: Array<{
    email?: string;
    exists?: boolean;
    usedByHospital?: { hospitalName?: string; hospitalId?: string } | string;
  }>;
  message?: string;
}

/**
 * Create AI email agent config (POST /v2/hospital/ai/email-agent)
 */
export const createAiEmailAgent = async (
  payload: AiEmailAgentPayload,
  options?: { hospitalId?: string },
): Promise<any> => {
  try {
    const headers: Record<string, string> = {};
    if (options?.hospitalId) {
      headers["X-Hospital-Id"] = options.hospitalId;
    }
    const response = await axios.post(
      `v2/hospital/ai/email-agent`,
      payload,
      Object.keys(headers).length ? { headers } : undefined,
    );
    if (response.data?.code === 200) {
      return response.data.data ?? null;
    }
    throw new Error(
      response.data?.message || "Failed to create AI email agent config",
    );
  } catch (error) {
    console.error("Failed to create AI email agent config:", error);
    throw error;
  }
};

/**
 * Update AI email agent config (PUT /v2/hospital/ai/email-agent)
 */
export const updateAiEmailAgent = async (
  payload: AiEmailAgentPayload,
  options?: { hospitalId?: string },
): Promise<any> => {
  try {
    const headers: Record<string, string> = {};
    if (options?.hospitalId) {
      headers["X-Hospital-Id"] = options.hospitalId;
    }
    const response = await axios.put(
      `v2/hospital/ai/email-agent`,
      payload,
      Object.keys(headers).length ? { headers } : undefined,
    );
    if (response.data?.code === 200) {
      return response.data.data ?? null;
    }
    throw new Error(
      response.data?.message || "Failed to update AI email agent config",
    );
  } catch (error) {
    console.error("Failed to update AI email agent config:", error);
    throw error;
  }
};

/**
 * Check AI email-agent sender conflicts (POST /v2/hospital/ai/email-agent/check-emails)
 */
export const checkAiEmailAgentEmails = async (
  payload: AiEmailAgentCheckPayload,
  options?: { hospitalId?: string },
): Promise<AiEmailAgentCheckResponse> => {
  try {
    const headers: Record<string, string> = {};
    if (options?.hospitalId) {
      headers["X-Hospital-Id"] = options.hospitalId;
    }
    const response = await axios.post(
      `v2/hospital/ai/email-agent/check-emails`,
      payload,
      Object.keys(headers).length ? { headers } : undefined,
    );
    if (response.data?.code === 200) {
      return response.data.data ?? {};
    }
    throw new Error(response.data?.message || "Failed to check sender emails");
  } catch (error) {
    console.error("Failed to check sender emails:", error);
    throw error;
  }
};

/**
 * Check AI email-agent domain conflicts (POST /v2/hospital/ai/email-agent/check-domains)
 */
export const checkAiEmailAgentDomains = async (
  payload: AiEmailAgentDomainCheckPayload,
  options?: { hospitalId?: string },
): Promise<AiEmailAgentDomainCheckResponse> => {
  try {
    const headers: Record<string, string> = {};
    if (options?.hospitalId) {
      headers["X-Hospital-Id"] = options.hospitalId;
    }
    const response = await axios.post(
      `v2/hospital/ai/email-agent/check-domains`,
      payload,
      Object.keys(headers).length ? { headers } : undefined,
    );
    if (response.data?.code === 200) {
      return response.data.data ?? {};
    }
    throw new Error(response.data?.message || "Failed to check sender domains");
  } catch (error) {
    console.error("Failed to check sender domains:", error);
    throw error;
  }
};

/**
 * Delete AI email agent config (DELETE /v2/hospital/ai/email-agent)
 */
export const deleteAiEmailAgent = async (options?: {
  hospitalId?: string;
}): Promise<any> => {
  try {
    const headers: Record<string, string> = {};
    if (options?.hospitalId) {
      headers["X-Hospital-Id"] = options.hospitalId;
    }
    const response = await axios.delete(
      `v2/hospital/ai/email-agent`,
      Object.keys(headers).length ? { headers } : undefined,
    );
    if (response.data?.code === 200) {
      return response.data.data ?? null;
    }
    throw new Error(
      response.data?.message || "Failed to delete AI email agent config",
    );
  } catch (error) {
    console.error("Failed to delete AI email agent config:", error);
    throw error;
  }
};

export interface AiEmailAgentActivityItem {
  messageId?: string;
  sender?: string;
  subject?: string;
  status?: string;
  reason?: string;
  shiftCount?: number;
  postedToHire?: boolean;
  createTime?: number;
}

/** Recent automatic email processing log (GET /v2/hospital/ai/email-agent/activity) */
export const getAiEmailAgentActivity = async (
  limit = 20,
): Promise<AiEmailAgentActivityItem[]> => {
  try {
    const response = await axios.get(`v2/hospital/ai/email-agent/activity`, {
      params: { limit },
    });
    if (response.data?.code === 200) {
      return Array.isArray(response.data.data) ? response.data.data : [];
    }
    throw new Error(
      response.data?.message || "Failed to fetch email processing activity",
    );
  } catch (error) {
    console.error("Failed to fetch email processing activity:", error);
    throw error;
  }
};

/**
 * Confirm an applicant for specific shift days
 */
export const confirmApplicantV2 = async (params: {
  shiftsId: string;
  userId: string;
  shiftsDaysIds?: string[];
  hospitalId?: string;
}): Promise<any> => {
  try {
    const url = `/v1/hospital/shifts/shifts-Confirmed/${params.shiftsId}/${params.userId}${params.hospitalId ? `?hospitalId=${params.hospitalId}` : ""}`;
    // Construct payload as comma-separated string in keyWord field
    const payload = {
      keyWord: (params.shiftsDaysIds || []).join(","),
    };

    const response = await axios.post(url, payload);

    if (response.data?.code === 200) {
      return response.data.data;
    } else {
      throw new Error(response.data?.message || "Failed to confirm applicant");
    }
  } catch (error) {
    console.error("Failed to confirm applicant:", error);
    throw error;
  }
};

// ============================================================================
// LEGACY CALLBACK-BASED API (Deprecated but still supported)
// ============================================================================

/**
 * @deprecated Use getApplicantDetailsV2 instead
 * Legacy callback-based wrapper for backward compatibility
 */
const api = {
  getList({ params = {} as any, callback = (() => {}) as any }) {
    if (params.type && typeof sessionStorage !== "undefined") {
      const cacheKey = "sys_list_v3_" + params.type;
      const cached = sessionStorage.getItem(cacheKey);
      if (cached) {
        try {
          const parsed = JSON.parse(cached);
          if (
            parsed?.data?.code === 200 &&
            Array.isArray(parsed?.data?.data) &&
            parsed.data.data.length > 0
          ) {
            return callback(parsed);
          }
        } catch (e) {}
        sessionStorage.removeItem(cacheKey);
      }
    }

    axios
      .get(`/v1/portal/sys/list-type/${params.type}`)
      .then((res) => {
        if (
          params.type &&
          res?.data?.code === 200 &&
          typeof sessionStorage !== "undefined"
        ) {
          sessionStorage.setItem(
            "sys_list_v3_" + params.type,
            JSON.stringify({ data: res.data }),
          );
        }
        callback(res);
      })
      .catch((err) => {
        callback(err);
      });
  },
  addShift({ params = {} as any, callback = (() => {}) as any }) {
    addShiftV2(params)
      .then((res) => callback({ data: { code: 200, data: res } }))
      .catch((err) => callback(err));
  },

  alterShift({ params = {} as any, callback = (() => {}) as any }) {
    editShiftV2(params.shiftsId, params.data)
      .then((res) => callback({ data: { code: 200, data: res } }))
      .catch((err) => callback(err));
  },

  listApi({
    params = {} as any,
    callback = (() => {}) as any,
    cancelToken = null as any,
  }) {
    listShiftsV2({ ...params, cancelToken })
      .then((res) => callback({ data: { code: 200, data: res } }))
      .catch((err) => {
        // Don't call callback for cancel errors - they're expected and should be silently ignored
        if (!AxiosBase.isCancel(err)) {
          callback(err);
        }
      });
  },

  getShift({ params = {} as any, callback = (() => {}) as any }) {
    getShiftDetailsV2(params.shiftsId, params.shiftsDaysId)
      .then((res) => callback({ data: { code: 200, data: res } }))
      .catch((err) => callback(err));
  },

  deleteSingleDateOfShift({
    params = {} as any,
    callback = (() => {}) as any,
  }) {
    deleteSingleShiftDateV2(params.shiftsId, params.shiftsDaysId)
      .then((res) => callback({ data: { code: 200, data: res } }))
      .catch((err) => callback(err));
  },

  deleteAllDateOfShift({ params = {} as any, callback = (() => {}) as any }) {
    deleteAllShiftDatesV2(params.shiftsId)
      .then((res) => callback({ data: { code: 200, data: res } }))
      .catch((err) => callback(err));
  },

  removeDoctorOfShift({ params = {} as any, callback = (() => {}) as any }) {
    removeDoctorFromShiftV2(
      params.ordersId,
      params.shiftsId,
      params.shiftsDaysId,
    )
      .then((res) => callback({ data: { code: 200, data: res } }))
      .catch((err) => callback(err));
  },

  getApplicants({ params = {} as any, callback = (() => {}) as any }) {
    getApplicantsListV2(params.shiftsId, params.shiftsDaysId)
      .then((res) => callback({ data: { code: 200, data: res } }))
      .catch((err) => callback(err));
  },

  getApplicant({ params = {} as any, callback = (() => {}) as any }) {
    getApplicantDetailsV2(params.shiftsId, params.shiftsDaysId, params.userId)
      .then((res) => callback({ data: { code: 200, data: res } }))
      .catch((err) => callback(err));
  },

  acceptApi({ params = {} as any, callback = (() => {}) as any }) {
    confirmApplicantV2({
      shiftsId: params.shiftsId,
      userId: params.userId,
      shiftsDaysIds: params.shiftsDaysIds,
    })
      .then((res) => callback({ data: { code: 200, data: res } }))
      .catch((err) => callback(err));
  },

  getDoctor({ params = {} as any, callback = (() => {}) as any }) {
    getDoctorV2(params.userId)
      .then((res) => callback({ data: { code: 200, data: res } }))
      .catch((err) => callback(err));
  },

  setAsFavorite({ params = {} as any, callback = (() => {}) as any }) {
    setFavoriteDoctorV2(params.userId)
      .then((res) => callback({ data: { code: 200, data: res } }))
      .catch((err) => callback(err));
  },

  removeAsFavorite({ params = {} as any, callback = (() => {}) as any }) {
    removeFavoriteDoctorV2(params.userId)
      .then((res) => callback({ data: { code: 200, data: res } }))
      .catch((err) => callback(err));
  },

  getShiftAllDate({ params = {} as any, callback = (() => {}) as any }) {
    getShiftAllDatesV2(params.shiftsId)
      .then((res) => callback({ data: { code: 200, data: res } }))
      .catch((err) => callback(err));
  },

  advertiseApi({ params = {} as any, callback = (() => {}) as any }) {
    reAdvertiseShiftV2(params.shiftsId, params.shiftsDaysId)
      .then((res) => callback({ data: { code: 200, data: res } }))
      .catch((err) => callback(err));
  },

  unCheck({ callback = (() => {}) as any }) {
    checkUnconfirmedApplicantsV2()
      .then((res) => callback({ data: { code: 200, data: res } }))
      .catch((err) => callback(err));
  },

  downloadApi({ params = {} as any, callback = (() => {}) as any }) {
    downloadFileV2(params)
      .then((res) => callback({ data: { code: 200, data: res } }))
      .catch((err) => callback(err));
  },

  bulkAddShifts({ params = {} as any, callback = (() => {}) as any }) {
    bulkAddShiftsV2(params)
      .then((res) => callback({ data: { code: 200, data: res } }))
      .catch((err) => callback(err));
  },

  getActiveApplicants({
    params = {} as any,
    callback = (() => {}) as any,
  } = {}) {
    getActiveApplicantsV2(params)
      .then((res) => callback({ data: { code: 200, data: res } }))
      .catch((err) => callback(err));
  },

  getPastApplicants({ params = {} as any, callback = (() => {}) as any } = {}) {
    getPastApplicantsV2(params)
      .then((res) => callback({ data: { code: 200, data: res } }))
      .catch((err) => callback(err));
  },

  listApiV2({
    params = {} as any,
    callback = (() => {}) as any,
    cancelToken = null as any,
  }) {
    listShiftsV2Advanced({ ...params, cancelToken })
      .then((res) => callback({ data: { code: 200, data: res } }))
      .catch((err) => {
        // Don't call callback for cancel errors - they're expected and should be silently ignored
        if (!AxiosBase.isCancel(err)) {
          callback(err);
        }
      });
  },

  listAiQueue({
    params = {} as any,
    callback = (() => {}) as any,
    cancelToken = null as any,
  }) {
    listHospitalAiQueue({
      pageNum: params.pageNum,
      pageSize: params.pageSize,
      cancelToken,
    })
      .then((res) => callback({ data: { code: 200, data: res } }))
      .catch((err) => {
        if (!AxiosBase.isCancel(err)) {
          callback(err);
        }
      });
  },

  listAiQueueGrouped({
    params = {} as any,
    callback = (() => {}) as any,
    cancelToken = null as any,
  }) {
    listHospitalAiQueueGrouped({
      pageNum: params.pageNum,
      pageSize: params.pageSize,
      includeItems: params.includeItems,
      cancelToken,
      masterScope: params.masterScope,
      inboxChannel: params.inboxChannel,
    })
      .then((res) => callback({ data: { code: 200, data: res } }))
      .catch((err) => {
        if (!AxiosBase.isCancel(err)) {
          callback(err);
        }
      });
  },

  listAiHistoryGrouped({
    params = {} as any,
    callback = (() => {}) as any,
    cancelToken = null as any,
  }) {
    listHospitalAiHistoryGrouped({
      pageNum: params.pageNum,
      pageSize: params.pageSize,
      includeItems: params.includeItems,
      cancelToken,
      masterScope: params.masterScope,
    })
      .then((res) => callback({ data: { code: 200, data: res } }))
      .catch((err) => {
        if (!AxiosBase.isCancel(err)) {
          callback(err);
        }
      });
  },

  confirmApplicant({ params = {} as any, callback = (() => {}) as any }) {
    confirmApplicantV2(params)
      .then((res) => callback({ data: { code: 200, data: res } }))
      .catch((err) => callback(err));
  },
};

export default api;
